import { JSX, useMemo } from "react";
import { useWindowSize } from "hooks/useWindowSize";
import classNames from "classnames";
import {
  flexRender,
  getCoreRowModel,
  HeaderGroup,
  Row,
  SortingState,
  Updater,
  useReactTable,
} from "react-table-8.10.7";
import { TableUI } from "components/design/TableUI";
import Table from "components/DesignSystem/Table/V2/Table";
import { colors } from "constants/colors";
import {
  RecognitionWaterfall,
  RecognitionWaterfallOrdering,
  RecognitionWaterfallSortColumn,
} from "store/apis/recognitionWaterfall";
import { DESCENDING_PREFIX, TOTAL_ROW_ID, WaterfallLabels } from "./constants";
import { SelectCell, useWaterfallColumns } from "./useWaterfallColumns";
import { pinningStyles, ROW_TYPE, toRows, WaterfallRow } from "./waterfallRows";

type WaterfallTableProps = {
  data: RecognitionWaterfall;
  labels: WaterfallLabels;
  selectedCellId: string | null;
  onSelectCell: SelectCell;
  ordering: RecognitionWaterfallOrdering;
  onOrderingChange: (ordering: RecognitionWaterfallOrdering) => void;
};

type WaterfallHeaderRowProps = {
  headerGroup: HeaderGroup<WaterfallRow>;
};

type WaterfallBodyRowProps = {
  row: Row<WaterfallRow>;
  selectedCellId: string | null;
};

// The API takes the sorted column's id, prefixed with - when descending
const toSorting = (ordering: RecognitionWaterfallOrdering): SortingState => [
  {
    id: ordering.replace(DESCENDING_PREFIX, ""),
    desc: ordering.startsWith(DESCENDING_PREFIX),
  },
];

const SORT_COLUMN_PATTERN =
  /^(name|prior_recognized|deferred|scheduled|period_\d+)$/;

const isSortColumn = (id: string): id is RecognitionWaterfallSortColumn =>
  SORT_COLUMN_PATTERN.test(id);

const toOrdering = ({
  id,
  desc,
}: SortingState[number]): RecognitionWaterfallOrdering | null => {
  if (!isSortColumn(id)) return null;
  return desc ? `${DESCENDING_PREFIX}${id}` : id;
};

// Lighter grey header row like the Revenue Waterfall's, with the name column pinned
const WaterfallHeaderRow = ({
  headerGroup,
}: WaterfallHeaderRowProps): JSX.Element => (
  <Table.Row className="t-bg-surface-lighter-grey">
    {headerGroup.headers.map((header) => (
      <Table.HeadCell
        key={header.id}
        colSpan={header.colSpan}
        isUpperCase={false}
        className={classNames({
          "t-drop-shadow-table-left-pin": header.column.getIsPinned(),
        })}
        style={{
          width: `${header.getSize()}px`,
          minWidth: `${header.column.getSize()}px`,
          ...pinningStyles(header.column, colors.surface["lighter-grey"]),
        }}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
      </Table.HeadCell>
    ))}
  </Table.Row>
);

const WaterfallBodyRow = ({
  row,
  selectedCellId,
}: WaterfallBodyRowProps): JSX.Element => {
  const isTotal = row.original.type === ROW_TYPE.TOTAL;
  const rowColor = isTotal
    ? colors.surface["lighter-grey"]
    : colors.surface.DEFAULT;

  return (
    <Table.Row className={classNames({ "t-font-semibold": isTotal })}>
      {row.getVisibleCells().map((cell) => (
        <Table.Cell
          key={cell.id}
          className={classNames("t-text-body-sm", {
            "t-drop-shadow-table-left-pin": cell.column.getIsPinned(),
          })}
          style={{
            width: `${cell.column.getSize()}px`,
            minWidth: `${cell.column.getSize()}px`,
            // The cell whose slider is open stays shaded, as in the Revenue Waterfall
            ...pinningStyles(
              cell.column,
              cell.id === selectedCellId ? colors.surface.grey : rowColor
            ),
          }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </Table.Cell>
      ))}
    </Table.Row>
  );
};

const DESKTOP_PINNING = { left: ["name"] };
// On phones a pinned name column fills the screen, so everything scrolls together instead.
const NO_PINNING = { left: [] };

export const WaterfallTable = ({
  data,
  labels,
  selectedCellId,
  onSelectCell,
  ordering,
  onOrderingChange,
}: WaterfallTableProps): JSX.Element => {
  const { isMobile } = useWindowSize();
  const sorting = toSorting(ordering);
  const handleSortingChange = (updater: Updater<SortingState>) => {
    const [next] = typeof updater === "function" ? updater(sorting) : updater;
    const nextOrdering = next && toOrdering(next);
    if (nextOrdering) onOrderingChange(nextOrdering);
  };

  const columns = useWaterfallColumns(data.periods, labels, onSelectCell);

  const tableData = useMemo(
    (): WaterfallRow[] => [
      ...toRows(data.parties, labels),
      {
        type: ROW_TYPE.TOTAL,
        id: TOTAL_ROW_ID,
        label: "Total",
        amounts: data.totals,
      },
    ],
    [data, labels]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      columnPinning: isMobile ? NO_PINNING : DESKTOP_PINNING,
      sorting,
    },
    // Sorted by the API across every page; one column at a time, flipping between its two directions
    manualSorting: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    onSortingChange: handleSortingChange,
    getRowId: (row, index) => `${row.id}-${index}`,
    getCoreRowModel: getCoreRowModel(),
  });

  const renderHeader = ({
    headerGroup,
  }: {
    headerGroup: HeaderGroup<WaterfallRow>;
  }) => <WaterfallHeaderRow key={headerGroup.id} headerGroup={headerGroup} />;

  const renderRow = ({ row }: { row: Row<WaterfallRow> }) => (
    <WaterfallBodyRow key={row.id} row={row} selectedCellId={selectedCellId} />
  );

  return (
    <div className="t-overflow-x-auto t-border-neutral-10 t-border-solid t-border t-text-text-60 t-rounded-lg t-overflow-y-hidden">
      <TableUI
        sizeType="absolute"
        table={table}
        size="small"
        sticky={false}
        selectedCell={selectedCellId}
        header={renderHeader}
      >
        {renderRow}
      </TableUI>
    </div>
  );
};
